import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@common/components/icons/CloseIcon';
import EditIcon from '@common/components/icons/EditIcon';
import RecurringScheduleIcon from '@common/components/icons/RecurringScheduleIcon';
import useModalBehavior from '@common/hooks/useModalBehavior';
import { utcTimeToLocal } from '@common/utils/dateUtils';
import { fetchScheduledReportsByDashboard } from '@api/scheduledReports';
import EnabledStatusBadge from '../EnabledStatusBadge';
import * as styles from './ScheduledReportsListModal.module.css';

/**
 * ScheduledReportsListModal Component
 *
 * Modal displaying scheduled reports for a specific dashboard/report template.
 * Supports two modes:
 * - Interactive mode (default): Card layout with Edit button and format badge, fetches data from API
 * - Read-only mode: Table layout with frequency and status, accepts data as props
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {number} dashboardId - The dashboard ID to fetch scheduled reports for (interactive mode)
 * @param {string} dashboardName - Name of the dashboard for the title
 * @param {boolean} readOnly - If true, shows table view without Run button
 * @param {Array} data - Pre-loaded data for read-only mode (optional)
 * @param {string} title - Custom title override (optional)
 */
export default function ScheduledReportsListModal({
  isOpen,
  onClose,
  dashboardId,
  dashboardName,
  readOnly,
  data,
  title,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scheduledReports, setScheduledReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use shared modal behavior hook for escape key and overlay click handling
  const { handleOverlayClick } = useModalBehavior(isOpen, onClose);

  // Use provided data in read-only mode, or fetch in interactive mode
  useEffect(() => {
    if (!isOpen) return;

    // In read-only mode with provided data, use that directly
    if (readOnly && data) {
      setScheduledReports(data);
      return;
    }

    // In interactive mode, fetch from API
    if (!dashboardId) return;

    const loadScheduledReports = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchScheduledReportsByDashboard(dashboardId);
        if (result.status === 'success') {
          setScheduledReports(result.data);
        } else {
          setError(result.message || t('common.error'));
        }
      } catch (err) {
        setError(err.message || t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    loadScheduledReports();
  }, [isOpen, dashboardId, readOnly, data, t]);

  // Handle editing a scheduled report (navigate to edit screen)
  const handleEditReport = useCallback(
    (scheduledReport) => {
      if (readOnly) return;
      navigate(`/schedule/${scheduledReport.id}/edit`, {
        state: {
          scheduledReport,
        },
      });
      onClose();
    },
    [navigate, onClose, readOnly],
  );

  // Format date for display
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    // Check for invalid date or epoch date (1970-01-01)
    if (Number.isNaN(date.getTime()) || date.getTime() === 0) {
      return '-';
    }
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  // Format schedule type for display (converts UTC to local time)
  const formatScheduleType = useCallback(
    (scheduleType, scheduleTime) => {
      const typeLabel = t(`scheduledReports.schedule.${scheduleType}`) || scheduleType;
      const localTime = utcTimeToLocal(scheduleTime);
      return `${typeLabel} @ ${localTime}`;
    },
    [t],
  );

  // Format frequency for read-only table view
  const formatFrequency = useCallback(
    (frequency) => {
      if (!frequency) return '-';
      // Try to translate the frequency, fall back to raw value
      const translationKey = `scheduledReports.schedule.${frequency}`;
      const translated = t(translationKey);
      return translated !== translationKey ? translated : frequency;
    },
    [t],
  );

  if (!isOpen) return null;

  // Determine modal title
  const modalTitle = title
    || (readOnly
      ? t('reports.dependency.scheduledReportsTitle')
      : t('scheduledReports.scheduledReportsFor', { name: dashboardName }));

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        className={`${styles.modal} ${readOnly ? styles.modalWide : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="scheduled-reports-modal-title"
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 id="scheduled-reports-modal-title" className={styles.title}>
            {modalTitle}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Content */}
        <div className={styles.content}>
          {loading && (
            <div className={styles.loading}>{t('common.loading')}</div>
          )}

          {error && <div className={styles.error}>{error}</div>}

          {!loading && !error && scheduledReports.length === 0 && (
            <div className={styles.empty}>
              {t('scheduledReports.noScheduledReports')}
            </div>
          )}

          {!loading && !error && scheduledReports.length > 0 && (
            readOnly ? (
              // Read-only table view
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('common.name')}</th>
                    <th>{t('reports.dependency.columnFrequency')}</th>
                    <th>{t('reports.dependency.columnNextRun')}</th>
                    <th>{t('reports.dependency.columnStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledReports.map((report) => (
                    <tr key={report.id}>
                      <td className={styles.nameCell}>{report.name}</td>
                      <td className={styles.frequencyCell}>
                        {formatFrequency(report.frequency || report.scheduleType)}
                      </td>
                      <td className={styles.dateCell}>{formatDate(report.nextRun)}</td>
                      <td>
                        <EnabledStatusBadge
                          status={report.status}
                          namespace="scheduledReports"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              // Interactive card view
              <div className={styles.reportsList}>
                {scheduledReports.map((report) => (
                  <div key={report.id} className={styles.reportCard}>
                    <div className={styles.reportInfo}>
                      <h3 className={styles.reportName}>{report.name}</h3>
                      <p className={styles.reportDescription}>
                        {report.description || t('reports.noDescription')}
                      </p>
                      <div className={styles.reportMeta}>
                        <span className={styles.scheduleInfo}>
                          <RecurringScheduleIcon />
                          {formatScheduleType(report.scheduleType, report.scheduleTime)}
                        </span>
                        <span className={styles.reportDate}>
                          {t('scheduledReports.nextRun')}: {formatDate(report.nextRun)}
                        </span>
                      </div>
                    </div>
                    <div className={styles.reportActions}>
                      <button
                        type="button"
                        className={styles.editButton}
                        onClick={() => handleEditReport(report)}
                      >
                        <EditIcon />
                        {t('common.edit')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Bottom divider */}
        <div className={styles.divider} />

        {/* Footer spacer */}
        <div className={styles.footerSpacer} />
      </div>
    </div>
  );
}

ScheduledReportsListModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dashboardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dashboardName: PropTypes.string,
  readOnly: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      frequency: PropTypes.string,
      scheduleType: PropTypes.string,
      scheduleTime: PropTypes.string,
      nextRun: PropTypes.string,
      status: PropTypes.number,
    }),
  ),
  title: PropTypes.string,
};

ScheduledReportsListModal.defaultProps = {
  dashboardId: null,
  dashboardName: 'Report',
  readOnly: false,
  data: null,
  title: null,
};
